import React, { useState, useEffect } from "react";
import { Modal, Input, Select, message, Form } from "antd";
import PageLayout from "@components/pageLayout/PageLayout.react";
import api from "@/libs/api";
import "./equipment.css";

const EQUIPMENT_TYPES = [
  { value: "meter",      label: "Meter" },
  { value: "inverter",   label: "Inverter" },
  { value: "battery",    label: "Battery" },
  { value: "evcharger",  label: "EV Charger" },
];

const TYPE_COLORS = {
  meter:      { bg: "#dbeafe", color: "#2563eb" },
  inverter:   { bg: "#dcfce7", color: "#16a34a" },
  battery:    { bg: "#ecfdf5", color: "#059669" },
  evcharger:  { bg: "#fce7f3", color: "#db2777" },
};

export default function Equipment() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create mode
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchEquipment();
  }, []);

  const fetchEquipment = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/equipment/all");
      if (res.data.success) {
        setItems(res.data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch equipment:", err);
      message.error("Failed to load equipment");
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ controller: "NO" });
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    form.setFieldsValue({
      name: item.name,
      type: item.type,
      controller: item.controller,
      vendor: item.vendor || "",
      datasheet_url: item.datasheet_url || "",
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editing) {
        const res = await api.put(`/api/equipment/${editing.id}`, values);
        if (res.data.success) {
          message.success("Equipment updated");
          setModalOpen(false);
          fetchEquipment();
        }
      } else {
        const res = await api.post("/api/equipment", values);
        if (res.data.success) {
          message.success("Equipment created");
          setModalOpen(false);
          fetchEquipment();
        }
      }
    } catch (err) {
      if (err?.errorFields) return; // form validation error
      console.error("Save failed:", err);
      message.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDeprecate = (item) => {
    const isDeprecated = item.soft_delete === "YES";
    Modal.confirm({
      title: isDeprecated ? "Restore Equipment" : "Deprecate Equipment",
      content: isDeprecated
        ? `Restore "${item.name}"? It will become available for onboarding again.`
        : `Deprecate "${item.name}"? It will no longer appear in onboarding dropdowns.`,
      okText: isDeprecated ? "Restore" : "Deprecate",
      okButtonProps: { danger: !isDeprecated },
      onOk: async () => {
        try {
          const res = await api.patch(`/api/equipment/${item.id}/deprecate`);
          if (res.data.success) {
            message.success(res.data.message);
            fetchEquipment();
          }
        } catch (err) {
          console.error("Deprecate failed:", err);
          message.error("Action failed");
        }
      },
    });
  };

  const activeCount = items.filter((i) => i.soft_delete === "NO").length;
  const deprecatedCount = items.filter((i) => i.soft_delete === "YES").length;

  return (
    <PageLayout active="equipment">
      <div className="eq-page">
        <div className="eq-header">
          <div>
            <h1 className="eq-title">Equipment Catalog</h1>
            <p className="eq-subtitle">
              Manage hardware types available for system onboarding.
              Deprecated equipment is hidden from onboarding but preserved here.
            </p>
          </div>
          <div className="eq-header-stats">
            <span className="eq-stat active">{activeCount} Active</span>
            {deprecatedCount > 0 && (
              <span className="eq-stat deprecated">{deprecatedCount} Deprecated</span>
            )}
          </div>
        </div>

        <div className="eq-table-section">
          <div className="eq-table-header">
            <h2 className="eq-section-title">All Equipment</h2>
            <button className="eq-btn-add" onClick={openCreate}>
              + Add Equipment
            </button>
          </div>

          {loading ? (
            <div className="eq-loading">Loading...</div>
          ) : (
            <div className="eq-table-wrapper">
              <table className="eq-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Controller</th>
                    <th>Vendor</th>
                    <th>Datasheet</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const typeStyle = TYPE_COLORS[item.type] || { bg: "#f3f4f6", color: "#6b7280" };
                    const isDeprecated = item.soft_delete === "YES";
                    return (
                      <tr key={item.id} className={isDeprecated ? "eq-row-deprecated" : ""}>
                        <td className="eq-td-name">{item.name}</td>
                        <td>
                          <span
                            className="eq-type-badge"
                            style={{ background: typeStyle.bg, color: typeStyle.color }}
                          >
                            {item.type}
                          </span>
                        </td>
                        <td>
                          {item.controller === "YES" ? (
                            <span className="eq-controller-badge yes">Controller</span>
                          ) : (
                            <span className="eq-controller-badge no">—</span>
                          )}
                        </td>
                        <td className="eq-td-secondary">{item.vendor || "—"}</td>
                        <td>
                          {item.datasheet_url ? (
                            <a
                              href={item.datasheet_url}
                              target="_blank"
                              rel="noreferrer"
                              className="eq-link"
                            >
                              View
                            </a>
                          ) : (
                            <span className="eq-td-secondary">—</span>
                          )}
                        </td>
                        <td>
                          <div className="eq-status-toggle">
                            <button
                              className={`eq-toggle-btn left ${!isDeprecated ? "selected" : ""}`}
                              onClick={() => isDeprecated && handleDeprecate(item)}
                            >
                              Active
                            </button>
                            <button
                              className={`eq-toggle-btn right ${isDeprecated ? "selected" : ""}`}
                              onClick={() => !isDeprecated && handleDeprecate(item)}
                            >
                              Deprecated
                            </button>
                          </div>
                        </td>
                        <td>
                          <button className="eq-btn-edit" onClick={() => openEdit(item)}>
                            Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {items.length === 0 && (
                    <tr>
                      <td colSpan={7} className="eq-empty">No equipment found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <Modal
          title={editing ? `Edit — ${editing.name}` : "Add Equipment"}
          open={modalOpen}
          onOk={handleSave}
          onCancel={() => setModalOpen(false)}
          okText={editing ? "Save" : "Create"}
          confirmLoading={saving}
          width={520}
        >
          <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
            <Form.Item
              label="Name"
              name="name"
              rules={[{ required: true, message: "Name is required" }]}
            >
              <Input placeholder="e.g. Tesla Powerwall 3" />
            </Form.Item>
            <Form.Item
              label="Type"
              name="type"
              rules={[{ required: true, message: "Type is required" }]}
            >
              <Select options={EQUIPMENT_TYPES} placeholder="Select type" />
            </Form.Item>
            <Form.Item
              label="Is Controller"
              name="controller"
              rules={[{ required: true }]}
            >
              <Select
                options={[
                  { value: "YES", label: "Yes — can be used as system controller" },
                  { value: "NO", label: "No — peripheral device" },
                ]}
              />
            </Form.Item>
            <Form.Item label="Vendor" name="vendor">
              <Input placeholder="e.g. Tesla" />
            </Form.Item>
            <Form.Item label="Datasheet URL" name="datasheet_url">
              <Input placeholder="https://..." />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </PageLayout>
  );
}
